import React from 'react';
import { orderBy } from 'lodash';
import styled from '@emotion/styled';
import Grid from '@mui/material/Grid';
import { Theme, ThemeProps } from '@theme/base';
import { withTheme } from '@emotion/react';
import { ContentDestination } from '@model/content';
import { TripTypes } from '@model/common';
import { SectionTitle, SectionTitleSizes } from '@components/common/section-title';
import { Button, ButtonColor, ButtonSize, ButtonVariant } from '@components/material-ui';
import { TestId } from '@components/test-ids';
import { RegionLink } from '@components/common/next-link/region-link';
import { ResortsLink } from '@components/common/next-link/resorts-link';
import { Tile } from '@components/explore-resorts/tile/Tile';

/* ***************** *
 *       Types       *
 * ***************** */

export interface DestinationListProps {
  theme?: Theme;
  testId?: string;
  title: string;
  ctaTitle?: string | null;
  path: string;
  destinations: Array<ContentDestination>;
  className?: string;
  tripType?: TripTypes;
  isResortList?: boolean;
}

export const getTileSize = (count: number, index: number) => {
  switch (count) {
    case 1:
      return 12;
    case 2:
      return 6;
    case 3:
      return index === 0 ? 12 : 6;
    default:
      return 6;
  }
};

/* ***************** *
 *       Styles      *
 * ***************** */
const Container = styled.div({});

const ViewMoreDestinationsButton = styled(Button)(({ theme }: ThemeProps) => ({
  marginTop: theme.custom.spacing.xxLarge
}));

const MAX_ITEMS: number = 4;

/* ********************************* *
 *    DestinationList                 *
 * ********************************* */

export const DestinationListComponent = ({
  theme,
  testId,
  destinations,
  title,
  ctaTitle,
  path,
  className,
  tripType,
  isResortList
}: DestinationListProps) => {
  const topDestinations = orderBy(destinations, ['hotelCount', 'title'], ['desc', 'asc']).slice(0, MAX_ITEMS);
  const totalTopDestinations: number = topDestinations.length;
  const totalDestinations: number = destinations.length;
  if (!totalDestinations) return null;

  const LinkComponent = isResortList ? ResortsLink : RegionLink;
  return (
    <Container className={className} data-testid={testId || TestId.destinationList.main}>
      <SectionTitle heading={title} size={SectionTitleSizes.LARGE} />
      <Grid container spacing={theme!.custom.gridSpacing.default}>
        {topDestinations.map((destination: ContentDestination, index: number) => (
          <Grid item xs={12} sm={getTileSize(totalTopDestinations, index)} key={destination.path}>
            <Tile
              name={destination.title}
              count={destination.hotelCount}
              path={destination.path}
              tripType={isResortList ? undefined : tripType || TripTypes.HOLIDAYS}
            />
          </Grid>
        ))}
      </Grid>
      {totalDestinations > 4 && ctaTitle && (
        <LinkComponent path={path}>
          <ViewMoreDestinationsButton
            variant={ButtonVariant.OUTLINED}
            size={ButtonSize.REGULAR}
            color={ButtonColor.SECONDARY}
            label={{
              label: ctaTitle,
              vars: { destinationName: title }
            }}
          />
        </LinkComponent>
      )}
    </Container>
  );
};

export const DestinationList = withTheme(DestinationListComponent);
